import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import * as Yup from "yup";
import ErrorModal from "../../components/Error/Error";
import Loading from "../../components/Loading/Loading";
import Map from "../../components/Map/Map";
import { useData } from "../../context/DataProvider";
import { IletisimService } from "../../services/iletisim.service";
import styles from "./ContactPage.module.scss";

interface IContactData {
  eposta: string;
  instagram: string;
  twitter: string;
  youtube: string;
  linkedin: string;
  facebook: string;
  adres: string;
  haritaUrl: string; // iframe için dangerouslySetInnerHTML kullanılacak
}

/* Mock data
const contactData: IContactData = {
  eposta: "guzelizmirhareketii@gmail.com",
  instagram: "https://instagram.com/...",
  twitter: "https://twitter.com/...",
  youtube: "https://youtube.com/...",
  linkedin: "https://linkedin.com/...",
  facebook: "https://facebook.com/...",
  adres: "Yeşiltepe Mahallesi, Halil Rıfatpaşa Caddesi, Sara Binası, Konak/İZMİR",
  haritaUrl: `<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d195.35321897714985!2d27.140192!3d38.4264179!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14bbd85c481d1519%3A0xbce2e9bd238ba832!2zRUdFxZ5FSMSwUiBBLsWeLiAtRWdlxZ9laGlyIFlhcMSxIHBsYW5sYW1hIE3DvMWfYXZpcmxpayB2ZSBUZWtub2xvamkgQS7Fni4!5e0!3m2!1str!2str!4v1728369180827!5m2!1str!2str" height="100%" loading="lazy" width="100%" allowFullScreen ></iframe>`,
};
*/

const validationSchema = Yup.object().shape({
  adSoyad: Yup.string()
    .required("Adınız ve soyadınız zorunludur")
    .min(2, "Adınız soyadınız geçerli olmalıdır")
    .max(32, "Adınız soyadınız en fazla 32 karakter olabilir")
    .matches(
      /^[a-zA-ZığüşöçİĞÜŞÖÇ\s]+$/,
      "Adınız soyadınız sadece harflerden oluşmalıdır"
    ),
  telefon: Yup.string()
    .required("Telefon numaranız zorunludur")
    .matches(
      /^\+90\s\d{3}\s\d{3}\s\d{4}$/,
      "Telefon numaranız eksik veya hatalı"
    )
    .max(16, "Telefon numaranız hatalı"),
  baslik: Yup.string().required("Açıklamanızın başlığı zorunludur"),
  aciklama: Yup.string().required("Açıklamanız zorunludur"),
});

const ContactPage: React.FC = () => {
  const { iletisimBilgileri: contactData, isLoading, error } = useData();
  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState({
    adSoyad: "",
    telefon: "",
    baslik: "",
    aciklama: "",
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorModal message={error.message} />;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 0) {
      if (!value.startsWith("90")) {
        value = "90" + value;
      }
      value = "+" + value.substring(0, 12);
      value = value.replace(/(\+\d{2})(\d{3})(\d{3})(\d{4})/, "$1 $2 $3 $4");
    }
    setFormData({ ...formData, telefon: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      await IletisimService.iletisimFormEkle({
        adSoyad: formData.adSoyad,
        telefonNo: formData.telefon,
        baslik: formData.baslik,
        aciklama: formData.aciklama,
      });

      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Mesajınız başarıyla gönderildi",
        life: 3000,
      });

      // Form verilerini sıfırla
      setFormData({
        adSoyad: "",
        telefon: "",
        baslik: "",
        aciklama: "",
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Hata",
            detail: err.message,
            life: 3000,
          });
        });
        return;
      }

      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Mesajınız gönderilirken bir hata oluştu",
        life: 3000,
      });
    }
  };

  return (
    <div className={styles.container}>
      <Toast ref={toast} />
      <h1 className={styles.title}>İLETİŞİM KANALLARIMIZ</h1>
      <div className={styles.grid}>
        <div className={styles.leftSection}>
          <ul className={styles.contactList}>
            <li>
              <div className={styles.contactTitle}>· E-mail Adresimiz</div>
              <span style={{ marginLeft: "24px" }}>{contactData?.eposta}</span>
            </li>

            <li>
              <div className={styles.contactTitle}>
                · Sosyal Medya Hesaplarımız
              </div>
              <div className={styles.socialIcons}>
                {contactData?.instagram && (
                  <a
                    href={contactData.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/assets/insta-white.png" alt="Instagram" />
                  </a>
                )}
                {contactData?.twitter && (
                  <a
                    href={contactData.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/assets/x-white.png" alt="Twitter" />
                  </a>
                )}
                {contactData?.youtube && (
                  <a
                    href={contactData.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/assets/youtube-white.png"
                      style={{ width: "25px", height: "20px" }}
                      alt="YouTube"
                    />
                  </a>
                )}
                {contactData?.linkedin && (
                  <a
                    href={contactData.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/assets/linkedin-white.png" alt="LinkedIn" />
                  </a>
                )}
                {contactData?.facebook && (
                  <a
                    href={contactData.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/assets/facebook-white.png"
                      style={{ width: "11px", height: "20px" }}
                      alt="Facebook"
                    />
                  </a>
                )}
              </div>
            </li>

            <li>
              <div className={styles.contactTitle}>· Adresimiz:</div>
              <span style={{ marginLeft: "24px" }}>{contactData?.adres}</span>
            </li>
            <li className={styles.mapContainer}>
              <Map url={contactData?.haritaUrl || ""} />
            </li>
          </ul>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.formHeader}>
            <h2>İzmir'i birlikte güzelleştirelim.</h2>
            <p>Sen de harekete katılmak için bizimle iletişime geç!</p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label>Adınız ve Soyadınız</label>
              <input
                type="text"
                placeholder="Adınız ve Soyadınız"
                value={formData.adSoyad}
                onChange={(e) =>
                  setFormData({ ...formData, adSoyad: e.target.value })
                }
              />
            </div>

            <div className={styles.formGroup}>
              <label>Telefon Numaranız</label>
              <input
                type="tel"
                placeholder="+90"
                value={formData.telefon}
                onChange={handlePhoneChange}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Açıklamanızın Başlığı</label>
              <input
                type="text"
                placeholder="Açıklamanızın Başlığı"
                value={formData.baslik}
                onChange={(e) =>
                  setFormData({ ...formData, baslik: e.target.value })
                }
              />
            </div>

            <div className={styles.formGroup}>
              <label>Açıklamanız</label>
              <textarea
                placeholder="Açıklamanızı heyecanla bekliyoruz."
                rows={6}
                value={formData.aciklama}
                onChange={(e) =>
                  setFormData({ ...formData, aciklama: e.target.value })
                }
              />
            </div>

            <button type="submit" className={styles.submitButton}>
              Mesajı Gönder
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
